/**
 * Router racine — refonte 9.P.2.R2 vers data router (createBrowserRouter).
 *
 * Migration BrowserRouter declarative → createBrowserRouter + RouterProvider
 * pour activer `useBlocker` (stable v6.7+) consommé par
 * `lib/unsavedChanges/UnsavedChangesContext` qui prévient la perte de
 * modifications non sauvegardées.
 *
 * Structure :
 *   - <RootLayout> wrap toutes les routes avec <UnsavedChangesProvider>.
 *   - 404 contextuelle : avec shell si user connecté, fallback minimal sinon.
 *   - Healthcheck : `public/health.json` servi statiquement par Vite/Nginx.
 */

import { Navigate, Outlet, RouterProvider, createBrowserRouter } from "react-router-dom";

import { AppShell } from "./components/AppShell";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { ToastProvider } from "./components/Toast";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { UnsavedChangesProvider } from "./lib/unsavedChanges/UnsavedChangesContext";
import { AcceptInvitePage } from "./pages/auth/AcceptInvitePage";
import { ResetConfirmPage } from "./pages/auth/ResetConfirmPage";
import { BrokerDetailPage } from "./pages/brokers/BrokerDetailPage";
import { BrokerListPage } from "./pages/brokers/BrokerListPage";
import { CompanyCreatePage } from "./pages/companies/CompanyCreatePage";
import { CompanyDetailPage } from "./pages/companies/CompanyDetailPage";
import { CompanyEditPage } from "./pages/companies/CompanyEditPage";
import { CompanyListPage } from "./pages/companies/CompanyListPage";
import { DashboardPage } from "./pages/DashboardPage";
import { FormResponsesListPage } from "./pages/form-responses/FormResponsesListPage";
import { FormFillPage } from "./pages/form-templates/FormFillPage";
import { FormTemplateDetailPage } from "./pages/form-templates/FormTemplateDetailPage";
import { FormTemplateListPage } from "./pages/form-templates/FormTemplateListPage";
import { FormVersionEditorPage } from "./pages/form-templates/FormVersionEditorPage";
import { GuaranteeDetailPage } from "./pages/guarantees/GuaranteeDetailPage";
import { GuaranteeListPage } from "./pages/guarantees/GuaranteeListPage";
import { BrokerGuidePage } from "./pages/help/BrokerGuidePage";
import { InsurerDetailPage } from "./pages/insurers/InsurerDetailPage";
import { InsurerListPage } from "./pages/insurers/InsurerListPage";
import { EmailReceiptsPage } from "./pages/intake-events/EmailReceiptsPage";
import { IntakeEventDetailPage } from "./pages/intake-events/IntakeEventDetailPage";
import { IntakeEventUploadPage } from "./pages/intake-events/IntakeEventUploadPage";
import { ReceptionsPage } from "./pages/intake-events/ReceptionsPage";
import { VoluntaryEmailUploadPage } from "./pages/intake-events/VoluntaryEmailUploadPage";
import { IntakeLinkDetailPage } from "./pages/intake-links/IntakeLinkDetailPage";
import { IntakeLinkListPage } from "./pages/intake-links/IntakeLinkListPage";
import { ProjectProgressPage } from "./pages/internal/ProjectProgressPage";
import { QualityReportPage } from "./pages/internal/QualityReportPage";
import { LoginPage } from "./pages/LoginPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { ProjectIntakeMvpPage } from "./pages/projects/_intake_mvp/ProjectIntakeMvpPage";
import { ProjectComparisonPage } from "./pages/projects/ProjectComparisonPage";
import { ProjectDetailPage } from "./pages/projects/ProjectDetailPage";
import { ProjectEditPage } from "./pages/projects/ProjectEditPage";
import { ProjectListPage } from "./pages/projects/ProjectListPage";
import { ProjectReportPage } from "./pages/projects/ProjectReportPage";
import { ProjectScoringPage } from "./pages/projects/ProjectScoringPage";
import { UploadSubmissionPage } from "./pages/projects/UploadSubmissionPage";
import { PublicCensusRoute } from "./pages/public-census/PublicCensusRoute";
import { PrivacyNoticePage } from "./pages/public-intake/PrivacyNoticePage";
import { PublicIntakePage } from "./pages/public-intake/PublicIntakePage";
import { PrepareSendPage } from "./pages/quote-requests/PrepareSendPage";
import { RequestsNewPage } from "./pages/requests/RequestsNewPage";
import { StartRequestPage } from "./pages/requests/StartRequestPage";
import { ResetRequestPage } from "./pages/ResetRequestPage";
import { AgentIdentitiesListPage } from "./pages/settings/agent-identities/AgentIdentitiesListPage";
import { AgentIdentityDetailPage } from "./pages/settings/agent-identities/AgentIdentityDetailPage";
import { SettingsTenantPage } from "./pages/settings/SettingsTenantPage";
import { TeamPage } from "./pages/settings/TeamPage";
import { SubmissionDetailPage } from "./pages/submissions/SubmissionDetailPage";

/**
 * RootLayout — wrap toutes les routes avec providers context-router-dependent.
 * UnsavedChangesProvider est rendered ici parce qu'il consomme useBlocker
 * (data router), qui n'est dispo qu'à l'intérieur d'un Route element.
 */
function RootLayout() {
  return (
    <UnsavedChangesProvider>
      <Outlet />
    </UnsavedChangesProvider>
  );
}

/**
 * 404 contextuelle : si l'utilisateur est connecté, on affiche la 404
 * dans le shell pour préserver son contexte de navigation. Sinon, fallback
 * minimaliste.
 */
function NotFoundRoute() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="grid min-h-dvh place-items-center bg-slate-50 text-sm text-slate-500"
      >
        …
      </div>
    );
  }
  if (user) {
    return (
      <AppShell>
        <NotFoundPage />
      </AppShell>
    );
  }
  return <NotFoundPage />;
}

/**
 * Wrapper pour les routes protégées qui doivent être enfants d'`AppShell`.
 * Pattern Outlet via ProtectedRoute + AppShell composé dans une route element.
 */
function ProtectedShellLayout() {
  return (
    <ProtectedRoute>
      <AppShell>
        <Outlet />
      </AppShell>
    </ProtectedRoute>
  );
}

const router: ReturnType<typeof createBrowserRouter> = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      // Auth — pas de shell (layout dédié plein écran).
      { path: "/auth/login", element: <LoginPage /> },
      { path: "/auth/reset", element: <ResetRequestPage /> },
      { path: "/auth/reset/confirm", element: <ResetConfirmPage /> },
      { path: "/auth/accept-invite", element: <AcceptInvitePage /> },

      // 9.N.2 — Public intake F2. NO AUTH, NO SHELL.
      { path: "/intake/:token", element: <PublicIntakePage /> },
      { path: "/privacy", element: <PrivacyNoticePage /> },
      // 9.BK.1 — la liste d'employés. Même coque publique, même anti-énumération.
      { path: "/census/:token", element: <PublicCensusRoute /> },

      // Zone protégée — wrap dans le shell.
      {
        element: <ProtectedShellLayout />,
        children: [
          { path: "/", element: <DashboardPage /> },
          { path: "/help", element: <BrokerGuidePage /> },

          // 9.T.1 — Unified "new request" entry surfacing the 3 humain intake
          // paths (public link / direct create / from PDF). UX orchestration
          // only — zero backend. Cf. docs/tests/chunk9t1-*.md.
          { path: "/requests/new", element: <RequestsNewPage /> },

          // Companies
          { path: "/companies", element: <CompanyListPage /> },
          { path: "/companies/new", element: <CompanyCreatePage /> },
          { path: "/companies/:id", element: <CompanyDetailPage /> },
          { path: "/companies/:id/edit", element: <CompanyEditPage /> },

          // Chunk 9.V.2b — carnet de contacts assureur (fiche + section contacts).
          { path: "/insurers", element: <InsurerListPage /> },
          { path: "/insurers/:id", element: <InsurerDetailPage /> },

          // Formulaires (F2-Forms-1c) — moteur de modèles de formulaire.
          // 1c-i : liste + création + détail/métadonnées + archivage.
          // La route d'édition /formulaires/:id/versions/:versionId/edit arrive
          // en 1c-ii (avec EDITOR_ROUTE_ENABLED basculé dans le même commit).
          { path: "/formulaires", element: <FormTemplateListPage /> },
          { path: "/formulaires/:id", element: <FormTemplateDetailPage /> },
          // 1c-ii : éditeur de schéma du brouillon (route + EDITOR_ROUTE_ENABLED
          // activés ensemble). Édition sections/champs/options/validations.
          {
            path: "/formulaires/:id/versions/:versionId/edit",
            element: <FormVersionEditorPage />,
          },
          // 1d-ii : remplissage conseiller d'une version publiée → bascule dossier
          // (CTA « Remplir » de la fiche détail + route montés dans le même commit).
          {
            path: "/formulaires/:id/versions/:versionId/remplir",
            element: <FormFillPage />,
          },
          // 9.U.7 : registre global des réponses (liste + filtres + export CSV).
          { path: "/reponses", element: <FormResponsesListPage /> },

          // 9.AZ.1 — le parcours d'envoi d'une demande, sur son propre chemin :
          // profond-lié, partageable, et repris tel quel au retour.
          { path: "/quote-requests/:id/preparer", element: <PrepareSendPage /> },
          { path: "/quote-requests/:id/parcours", element: <PrepareSendPage /> },
          { path: "/requests/new/start", element: <StartRequestPage /> },

          // Projects
          { path: "/projects", element: <ProjectListPage /> },
          { path: "/projects/new", element: <ProjectIntakeMvpPage /> },
          { path: "/projects/:id", element: <ProjectDetailPage /> },
          { path: "/projects/:id/edit", element: <ProjectEditPage /> },
          // 9.Q.1 — page comparaison soumissions (matrice brute read-only)
          { path: "/projects/:id/comparison", element: <ProjectComparisonPage /> },
          // 9.AI.1 — rapport conseiller/client. Le contenu se reconstruit depuis la
          // même lecture que la matrice ; la page ne recalcule aucune comparaison.
          { path: "/projects/:id/report", element: <ProjectReportPage /> },
          // 9.AJ.1 — classement pondéré. Aide à la décision du conseiller : même
          // lecture que le comparateur, aucun recalcul côté navigateur.
          { path: "/projects/:id/scoring", element: <ProjectScoringPage /> },
          {
            path: "/projects/:projectId/submissions/new",
            element: <UploadSubmissionPage />,
          },

          // Submissions
          { path: "/submissions/:id", element: <SubmissionDetailPage /> },

          // Intake links broker UI (9.N.3)
          { path: "/intake-links", element: <IntakeLinkListPage /> },
          { path: "/intake-links/:id", element: <IntakeLinkDetailPage /> },

          // Intake events broker upload + détail (9.T.3) — orphan events
          // créés via PDF upload broker, résolution manuelle.
          { path: "/intake-events", element: <ReceptionsPage /> },
          { path: "/email-receipts", element: <EmailReceiptsPage /> },
          { path: "/intake-events/email", element: <VoluntaryEmailUploadPage /> },
          { path: "/intake-events/new", element: <IntakeEventUploadPage /> },
          { path: "/intake-events/:id", element: <IntakeEventDetailPage /> },

          // Brokers
          { path: "/brokers", element: <BrokerListPage /> },
          { path: "/brokers/:id", element: <BrokerDetailPage /> },

          // Guarantees
          { path: "/guarantees", element: <GuaranteeListPage /> },
          { path: "/guarantees/:code", element: <GuaranteeDetailPage /> },

          // Settings
          {
            path: "/settings",
            element: <Navigate to="/settings/tenant" replace />,
          },
          { path: "/settings/tenant", element: <SettingsTenantPage /> },

          // 9.AE.1 — équipe du tenant. Sous `/settings` parce que c'est de
          // l'administration du cabinet, pas du travail sur un dossier.
          { path: "/settings/team", element: <TeamPage /> },

          // 9.M.7 — Agent identities mgmt (super_admin only via page-level
          // RBAC guard `<Navigate to="/" />` ; backend RLS double check sur
          // POST/PATCH `is_super_admin()`).
          {
            path: "/settings/agent-identities",
            element: <AgentIdentitiesListPage />,
          },
          {
            path: "/settings/agent-identities/:id",
            element: <AgentIdentityDetailPage />,
          },

          // 9.R.1 — Internal project progress docs viewer (3 docs allowlist :
          // plan-global / project-status / dettes). Logged-in only via
          // ProtectedShellLayout. Allowlist runtime côté page handler.
          {
            path: "/internal/progress/:slug",
            element: <ProjectProgressPage />,
          },

          // 9.R.2 — Internal quality report (static JSON snapshot, Phase 1
          // sans exécution live de tests depuis le navigateur). Allowlist EXACTE
          // de 1 fichier `apps/web/src/generated/quality-report.json`.
          {
            path: "/internal/quality/tests",
            element: <QualityReportPage />,
          },
        ],
      },

      // 404 contextuelle — wrappée par RootLayout (donc avec UnsavedChangesProvider).
      { path: "*", element: <NotFoundRoute /> },
    ],
  },
]);

// Export pour tests RTL qui veulent re-créer un router local (createMemoryRouter
// avec les mêmes routes shape). Aucun risque code applicatif — seul le router
// instance est importé.
export { router };

/**
 * Wrapper externe au RouterProvider : ErrorBoundary + AuthProvider +
 * ToastProvider sont des contexts qui ne dépendent pas du router. On les
 * monte au-dessus de RouterProvider pour qu'ils englobent toutes les routes
 * sans être recréés à chaque navigation.
 *
 * Note : `useNavigate` etc. ne sont pas accessibles dans ces providers
 * car rendered hors RouterProvider. C'est OK : AuthProvider expose
 * signIn/signOut + state, sans navigation interne. Les pages qui consomment
 * useNavigate l'utilisent dans leurs propres handlers (post-mount inside Route).
 */
export function App() {
  return (
    <ErrorBoundary>
      <ToastProvider>
        <AuthProvider>
          <RouterProvider router={router} />
        </AuthProvider>
      </ToastProvider>
    </ErrorBoundary>
  );
}

// Re-export pour compat éventuelle (ProtectedShellLayout exporté pour tests).
export { ProtectedShellLayout, NotFoundRoute, RootLayout };
